import React from 'react';
import {
    Typography,
    Box,
    Grid,
    Chip,
    Paper,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    InfoOutlined as InfoOutlinedIcon,
    AccountBalanceOutlined as AccountBalanceOutlinedIcon,
    EventNoteOutlined as EventNoteOutlinedIcon,
    PersonPinCircleOutlined as PersonPinCircleOutlinedIcon,
    SpeakerNotesOutlined as SpeakerNotesOutlinedIcon,
    DnsOutlined as DnsOutlinedIcon,
    CheckCircleOutline as CheckCircleOutlineIcon,
    HighlightOffOutlined as HighlightOffOutlinedIcon,
    Fingerprint as FingerprintIcon,
    MedicalServicesOutlined as MedicalServicesOutlinedIcon,
    SecurityOutlined as SecurityOutlinedIcon,
    VpnKeyOutlined as VpnKeyOutlinedIcon,
    VerifiedUserOutlined as VerifiedUserOutlinedIcon,
    FlagOutlined as FlagOutlinedIcon,
    WorkOutline as WorkOutlineIcon,
    ConfirmationNumberOutlined as ConfirmationNumberOutlinedIcon,
    EmailOutlined as EmailOutlinedIcon
} from '@mui/icons-material';
import { formatDate } from './CaseDetailModal'; // Re-use the robust formatDate

const displayData = (data, prefix = '', suffix = '') => {
    if (data === null || typeof data === 'undefined' || data === '') {
        return 'N/A';
    }
    if (typeof data === 'boolean') {
        return data ? 'Yes' : 'No';
    }
    return `${prefix}${data}${suffix}`;
};

const displayBoolean = (value, trueLabel = 'Yes', falseLabel = 'No') => {
    if (value === null || typeof value === 'undefined') {
        return 'N/A';
    }
    return value ? (
        <Chip icon={<CheckCircleOutlineIcon />} label={trueLabel} color="success" size="small" variant="outlined" />
    ) : (
        <Chip icon={<HighlightOffOutlinedIcon />} label={falseLabel} color="error" size="small" variant="outlined" />
    );
};

const TimelineDetailView = ({ timelineData }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    if (!timelineData) {
        return null;
    }

    const {
        applicationStream,
        province,
        isExpressEntry,
        crsScoreAtITA,
        nocCode,
        countryOfResidence,
        itaDate,
        securityCheck,
        birthCountry,
        pnpProvince,
        isPnp,
        pnpNominationDate,
        applicationSubmittedDate,
        aorDate,
        biometricsInstructionLetterDate,
        biometricsSubmittedDate,
        medicalExamInstructionDate,
        medicalExamPassedDate,
        backgroundCheckStartDate,
        backgroundCheckCompleteDate,
        portal1Date,
        portal2Date,
        eCoprDate,
        finalDecisionDate,
        notes,
        submissionDate,
        updatedAt,
        email,
        hideEmail,
      } = timelineData;

      const sections = [
        {
            title: 'Application Snapshot',
            icon: <PersonPinCircleOutlinedIcon sx={{ color: theme.palette.primary.main }} />,
            items: [
                { icon: <InfoOutlinedIcon />, label: 'Application Stream', value: displayData(applicationStream) },
                { icon: <FlagOutlinedIcon />, label: 'Province of Application', value: displayData(province) },
                { icon: <ConfirmationNumberOutlinedIcon />, label: 'Express Entry?', value: displayBoolean(isExpressEntry) },
                { icon: <ConfirmationNumberOutlinedIcon />, label: 'CRS at ITA', value: displayData(crsScoreAtITA) },
                { icon: <WorkOutlineIcon />, label: 'NOC Code', value: displayData(nocCode) },
                { icon: <FlagOutlinedIcon />, label: 'Country of Residence', value: displayData(countryOfResidence) },
                { icon: <FlagOutlinedIcon />, label: 'Birth Country', value: displayData(birthCountry) },
                ...(email && !hideEmail ? [{ icon: <EmailOutlinedIcon />, label: 'Email', value: displayData(email) }] : []),
              ],
            },
        ...(isPnp ? [{
            title: 'Provincial Nomination Program (PNP)',
            icon: <AccountBalanceOutlinedIcon sx={{ color: theme.palette.secondary.main }} />,
            items: [
                { icon: <AccountBalanceOutlinedIcon />, label: 'PNP Stream/Province', value: displayData(pnpProvince || province) },
                { icon: <EventNoteOutlinedIcon />, label: 'PNP Nomination Date', value: formatDate(pnpNominationDate) },
            ],
        }] : []),
        {
            title: 'Processing Milestones',
            icon: <EventNoteOutlinedIcon sx={{ color: theme.palette.info.main }} />,
            items: [
                { icon: <EventNoteOutlinedIcon />, label: 'ITA Date', value: formatDate(itaDate) },
                { icon: <EventNoteOutlinedIcon />, label: 'Application Submitted', value: formatDate(applicationSubmittedDate) },
                { icon: <EventNoteOutlinedIcon />, label: 'AOR Date', value: formatDate(aorDate) },
                { icon: <FingerprintIcon />, label: 'Biometrics Letter', value: formatDate(biometricsInstructionLetterDate) },
                { icon: <FingerprintIcon />, label: 'Biometrics Submitted', value: formatDate(biometricsSubmittedDate) },
                { icon: <MedicalServicesOutlinedIcon />, label: 'Medicals Instructed', value: formatDate(medicalExamInstructionDate) },
                { icon: <MedicalServicesOutlinedIcon />, label: 'Medicals Passed', value: formatDate(medicalExamPassedDate) },
                { icon: <SecurityOutlinedIcon />, label: 'Background Check Start', value: formatDate(backgroundCheckStartDate) },
                { icon: <SecurityOutlinedIcon />, label: 'Background Check Complete', value: formatDate(backgroundCheckCompleteDate) },
                { icon: <SecurityOutlinedIcon />, label: 'Security Check Status', value: displayData(securityCheck) },
                { icon: <VpnKeyOutlinedIcon />, label: 'Portal 1 (P1)', value: formatDate(portal1Date) },
                { icon: <VpnKeyOutlinedIcon />, label: 'Portal 2 (P2)', value: formatDate(portal2Date) },
                { icon: <VerifiedUserOutlinedIcon />, label: 'eCoPR Date', value: formatDate(eCoprDate) },
                { icon: <VerifiedUserOutlinedIcon />, label: 'Final Decision Date', value: formatDate(finalDecisionDate) },
            ],
        },
        ...(notes ? [{
            title: 'Applicant Notes',
            icon: <SpeakerNotesOutlinedIcon sx={{ color: theme.palette.text.secondary }} />,
            items: [
                { fullWidth: true, label: 'Notes', value: displayData(notes) }
            ]
        }] : []),
        {
            title: 'Additional Details',
            icon: <InfoOutlinedIcon sx={{ color: theme.palette.grey[600] }} />,
            items: [
                { icon: <ConfirmationNumberOutlinedIcon />, label: 'CRS at ITA', value: displayData(crsScoreAtITA) },
                { icon: <WorkOutlineIcon />, label: 'NOC Code', value: displayData(nocCode) },
                { icon: <FlagOutlinedIcon />, label: 'Country of Residence', value: displayData(countryOfResidence) },
                { icon: <FlagOutlinedIcon />, label: 'Birth Country', value: displayData(birthCountry) },
                { icon: <SecurityOutlinedIcon />, label: 'Security Check Status', value: displayData(securityCheck) },
            ].filter(item => item.value !== 'N/A'),
        },
        {
            title: 'System Information',
            icon: <DnsOutlinedIcon sx={{ color: theme.palette.text.secondary }} />,
            items: [
                { icon: <EventNoteOutlinedIcon />, label: 'Record Submitted', value: formatDate(submissionDate) },
                { icon: <EventNoteOutlinedIcon />, label: 'Last Updated', value: formatDate(updatedAt) },
            ],
        },
    ].filter(section => section.items.length > 0);

    return (
        <Box>
            {sections.map((section, sectionIndex) => {
                const visibleItems = section.items.filter(item =>
                    (typeof item.value === 'string' ? item.value !== 'N/A' : true)
                );
                if (visibleItems.length === 0 && section.title !== 'Applicant Notes') return null;

                return (
                    <Paper
                        key={sectionIndex}
                        elevation={isMobile ? 0 : 2}
                        variant={isMobile ? "outlined" : "elevation"}
                        sx={{
                            p: { xs: 1.5, sm: 2 },
                            mb: { xs: 2, sm: 2.5 },
                            borderRadius: isMobile ? '8px' : '12px',
                            borderLeft: isMobile ? `3px solid ${section.icon.props.sx?.color || theme.palette.primary.main}` : `4px solid ${section.icon.props.sx?.color || theme.palette.primary.main}`,
                            overflow: 'hidden'
                        }}
                    >
                        <Typography
                            variant="h6"
                            gutterBottom
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 1.5,
                                fontSize: isMobile ? '1rem' : '1.1rem',
                                fontWeight: '600',
                                color: section.icon.props.sx?.color || theme.palette.primary.main
                            }}
                        >
                            {React.cloneElement(section.icon, { sx: { ...section.icon.props.sx, mr: 1, fontSize: isMobile ? '1.2rem' : '1.4rem' }})}
                            {section.title}
                        </Typography>
                        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                            {visibleItems.map((item, itemIndex) => (
                                <Grid item xs={12} sm={item.fullWidth ? 12 : 6} md={item.fullWidth ? 12 : 4} key={itemIndex}>
                                    <Box display="flex" alignItems="flex-start">
                                        {item.icon && !item.fullWidth && React.cloneElement(item.icon, { sx: { mr: 1, mt: 0.5, fontSize: '1rem', color: 'text.secondary' }})}
                                        <Box flexGrow={1}>
                                            <Typography variant="subtitle2" component="div" sx={{ fontWeight: '600', color: 'text.primary', fontSize: '0.8rem', lineHeight: 1.4 }}>
                                                {item.label}:
                                            </Typography>
                                            <Typography variant="body2" component="div" sx={{ color: 'text.secondary', whiteSpace: item.fullWidth ? 'pre-wrap' : 'normal', fontSize: '0.9rem', wordBreak: 'break-word' }}>
                                                {item.value}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>
                );
            })}
        </Box>
    );
};

export default TimelineDetailView;